import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Box, TextFieldProps } from '@mui/material';
import { useUpdateNodeInternals } from 'reactflow';
import { useTranslation } from 'react-i18next';
import { NodeTextField } from '@/UI/NodeTextField/NodeTextField';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { FlexCol } from '@/UI/styles';
import { PromptConcatenatorData } from '@/types/node';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { getResizableDimensions } from '@/consts/node-ui.consts';
import { useNodeStringValue, usePromptConcatView } from '../Recipe/FlowContext';
import { hasEditingPermissions } from './Utils';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import { PROMPT_CONCAT_MAX_CHARS } from './consts/validations.consts';

interface PromptConcatProps {
  id: string;
  data: PromptConcatenatorData & { output?: { prompt: string } };
}
const PROMPT_CONCAT_RESIZABLE_DIMENSIONS = getResizableDimensions(120, 'large');
const GAP = 8;

function PromptConcat({ id, data }: PromptConcatProps) {
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const view = usePromptConcatView(id);
  const { handles, additionalPrompt, height } = data;

  const isHeightDefined = !!height;
  const textFieldMinHeight = isHeightDefined
    ? undefined
    : (PROMPT_CONCAT_RESIZABLE_DIMENSIONS.defaultTextareaHeight - GAP) / 2;
  const textFieldMaxHeight = isHeightDefined ? undefined : (textFieldMinHeight ?? 0) * 1.2;

  const [hasFocus, setHasFocus] = useState(false);
  const handleFocus = useCallback(() => setHasFocus(true), []);
  const handleBlur = useCallback(() => setHasFocus(false), []);

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => view.setAdditionalPrompt(evt.target.value, false),
    [view],
  );

  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    // this is to update the node internals when the input handles are exposed
    updateNodeInternals(id);
  }, [handles.input, id, updateNodeInternals]);

  const combinedPrompt = useNodeStringValue(id) ?? '';

  return (
    <DynamicNode2
      id={id}
      data={data}
      className="prompt"
      isResizable
      resizableDimensions={PROMPT_CONCAT_RESIZABLE_DIMENSIONS}
    >
      <FlexCol
        sx={{
          flex: 1,
          minHeight: 0,
          gap: `${GAP}px`,
          cursor: !hasEditingPermissions(role, data) ? 'default' : '',
          pointerEvents: !hasEditingPermissions(role, data) ? 'none' : 'auto',
        }}
      >
        <NodeTextField
          value={combinedPrompt}
          placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_CONCAT.PLACEHOLDER)}
          multiline
          minHeight={textFieldMinHeight}
          maxHeight={textFieldMaxHeight}
          fullWidth
          size="small"
          color={color.White64_T as TextFieldProps['color']}
          readOnly
          id={`concat1-${id}`}
        />
        <NodeTextField
          className={hasFocus ? 'nodrag' : ''}
          value={additionalPrompt}
          placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_CONCAT.ADDITIONAL_TEXT_PLACEHOLDER)}
          multiline
          minHeight={textFieldMinHeight}
          maxHeight={textFieldMaxHeight}
          fullWidth
          size="small"
          id={`concat2-${id}`}
          slotProps={{ input: { inputProps: { maxLength: PROMPT_CONCAT_MAX_CHARS } } }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </FlexCol>
      <Box sx={{ mt: 2, transition: 'all 0.1s ease-in-out' }}>
        <ButtonContained mode="text" size="small" onClick={() => view.addInputHandle()}>
          {t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_CONCAT.ADD_ANOTHER_TEXT_INPUT)}
        </ButtonContained>
      </Box>
    </DynamicNode2>
  );
}

export default PromptConcat;
